// AI Image Analysis Service
// Image search uses the backend: Voyage AI (when available) and/or OpenAI Vision.
// The website uploads the image only; no API keys are sent to the browser.

import { api } from './api';
import { sanitizeUserFacingError } from './userFacingError';

/**
 * JSON string for multipart field `catalogContext` — same shape the backend expects for OpenAI + Voyage.
 * @param {Array<{ id?: string, _id?: string, name?: string, slug?: string}>} categories
 * @param {Record<string, Array<{ id?: string, _id?: string, railwayId?: string, name?: string, slug?: string }>>} subcategoriesByCategory
 */
export function buildImageSearchCatalogContext(categories, subcategoriesByCategory) {
  if (!categories || categories.length === 0) return null;
  const cats = categories.map((c) => ({
    id: String(c.id != null ? c.id : c._id),
    name: c.name || '',
    slug: c.slug || ''
  }));
  const subs = [];
  for (const c of categories) {
    const cid = String(c.id != null ? c.id : c._id);
    const list = (subcategoriesByCategory && subcategoriesByCategory[cid]) || [];
    for (const s of list) {
      subs.push({
        id: String(s.id != null ? s.id : s._id),
        categoryId: cid,
        railwayId: s.railwayId != null ? String(s.railwayId) : '',
        name: s.name || '',
        slug: s.slug || ''
      });
    }
  }
  return JSON.stringify({ categories: cats, subcategories: subs });
}

function cleanImageSearchDescription(desc) {
  return String(desc || '')
    .replace(/\s*\(Voyage AI\)/gi, '')
    .replace(/\s*using Voyage AI\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function normalizeAnalysisResponse(data) {
  const matchingProducts = Array.isArray(data.matchingProducts) ? data.matchingProducts : [];
  const matchTier = data.matchTier || (matchingProducts.length ? 'unknown' : 'none');
  return {
    success: true,
    matchTier,
    data: {
      productName: data.productName || data.name || '',
      category: data.category || '',
      subCategory: data.subCategory || '',
      brand: data.brand || '',
      model: data.model || '',
      description: cleanImageSearchDescription(data.description),
      keywords: data.keywords || [],
      confidence: data.confidence || 0,
      attributes: data.attributes || {},
      color: data.color || '',
      material: data.material || '',
      type: data.type || '',
      matchTier
    },
    matchingProducts,
    voyageImageSearch: data.voyageImageSearch === true,
    voyageRerank: data.voyageRerank === true,
    voyageWeakMatch: data.voyageWeakMatch === true,
    voyageOpenAiBlend: data.voyageOpenAiBlend === true,
    voyageDiagnostics: data.voyageDiagnostics || null
  };
}

/**
 * Analyzes an image to find similar products. Tries Voyage AI image search first (when backend has
 * product image embeddings); otherwise uses OpenAI Vision + Voyage text rerank or OpenAI-only.
 * Sends only the image bytes; filename is never used.
 * @param {File} imageFile - The image file to analyze (only image content is used; filename is ignored)
 * @param {object} [options]
 * @param {string|null} [options.catalogContext] - JSON string { categories, subcategories } for guest/catalog mode
 * @returns {Promise<Object>} - Analysis results with product information and matchingProducts
 */
export const analyzeImage = async (imageFile, options = {}) => {
  try {
    const catalogContext = options.catalogContext || null;

    const makeFormData = () => {
      const fd = new FormData();
      fd.append('image', imageFile);
      if (catalogContext) {
        fd.append('catalogContext', catalogContext);
      }
      return fd;
    };

    // 1) Try Voyage-only image search (visual similarity). Uses product image embeddings if backfill was run.
    try {
      const voyageData = await api.postFormData('/products/analyze-image-voyage', makeFormData());
      if (voyageData && voyageData.success && Array.isArray(voyageData.matchingProducts) && voyageData.matchingProducts.length > 0) {
        return normalizeAnalysisResponse(voyageData);
      }
    } catch (_) {
      // Voyage endpoint unavailable or error; fall back to standard analyze-image
    }

    // 2) Standard path: OpenAI Vision + optional Voyage text rerank (when VOYAGE_API_KEY is set)
    const data = await api.postFormData('/products/analyze-image', makeFormData());
    if (!data.success && data.error) {
      return { success: false, error: sanitizeUserFacingError(data.message || data.error, 'Image analysis failed'), data: null, matchingProducts: [] };
    }
    return normalizeAnalysisResponse(data);
  } catch (error) {
    console.error('Image analysis error:', error);
    return {
      success: false,
      error: sanitizeUserFacingError(error.message, 'Image analysis failed'),
      data: null,
      matchingProducts: [],
    };
  }
};

/**
 * Fallback: Extract product information from filename
 * @param {string} filename - The image filename
 * @returns {Object} - Extracted information
 */
const extractFromFilename = (filename) => {
  const fileName = filename.toLowerCase();
  
  // Product keywords mapping
  const categoryKeywords = {
    electronics: ['phone', 'smartphone', 'laptop', 'tablet', 'ipad', 'headphone', 'earphone', 'speaker', 'watch', 'camera', 'monitor', 'keyboard', 'mouse', 'charger', 'cable', 'battery', 'powerbank'],
    fashion: ['shoe', 'sneaker', 'boot', 'bag', 'backpack', 'shirt', 'dress', 'jacket', 'coat', 'sunglass', 'glasses'],
    home: ['furniture', 'desk', 'chair', 'coffee', 'maker', 'machine', 'appliance', 'kitchen'],
    sports: ['yoga', 'mat', 'gym', 'equipment', 'fitness', 'sports'],
    beauty: ['skincare', 'beauty', 'cosmetic'],
    toys: ['toy', 'game', 'controller'],
    other: ['tool', 'kit', 'hardware', 'storage', 'drive', 'usb', 'book', 'notebook', 'pen', 'stationery', 'gadget', 'device', 'accessory']
  };

  // Find category
  let category = 'other';
  for (const [cat, keywords] of Object.entries(categoryKeywords)) {
    if (keywords.some(keyword => fileName.includes(keyword))) {
      category = cat;
      break;
    }
  }

  // Extract brand names (common brands)
  const brands = ['apple', 'samsung', 'nike', 'adidas', 'sony', 'lg', 'hp', 'dell', 'lenovo', 'huawei', 'xiaomi', 'oppo', 'vivo', 'oneplus'];
  const brand = brands.find(b => fileName.includes(b)) || '';

  // Extract meaningful words
  const words = fileName
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 3 && !['image', 'photo', 'picture', 'img', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'download', 'screenshot'].includes(w));

  const productName = words.length > 0 ? words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Product';

  return {
    productName,
    category,
    brand,
    description: `Product identified from image: ${productName}`,
    keywords: words.slice(0, 5),
    confidence: 0.3, // Low confidence for filename-based extraction
  };
};

/**
 * Search for products matching the analyzed image data
 * @param {Object} analysisData - The analysis results from AI
 * @param {Array} allProducts - All available products from database
 * @returns {Array} - Matching products sorted by relevance
 */
export const searchMatchingProducts = (analysisData, allProducts) => {
  if (!analysisData || !allProducts || allProducts.length === 0) {
    return [];
  }

  const { productName, keywords, brand, category } = analysisData;
  const searchTerms = [
    productName,
    ...keywords,
    brand
  ].filter(Boolean).map(term => term.toLowerCase());

  // Score products based on matches
  const scoredProducts = allProducts.map(product => {
    let score = 0;
    const productText = [
      product.name,
      product.description,
      product.brand,
      product.categoryName,
      product.categorySlug,
      product.category,
      product.tags?.join(' ') || ''
    ].filter(Boolean).map(x => typeof x === 'string' ? x : String(x)).join(' ').toLowerCase();

    // Exact matches
    searchTerms.forEach(term => {
      if (term && productText.includes(term)) {
        score += 10;
      }
      // Partial matches
      if (term && term.length >= 3 && productText.includes(term.substring(0, Math.max(3, term.length - 2)))) {
        score += 5;
      }
    });

    // Category match (compare with categoryName or categorySlug)
    const catStr = category ? category.toLowerCase() : '';
    const productCatStr = (product.categoryName || product.categorySlug || product.category || '').toString().toLowerCase();
    if (catStr && productCatStr && (productCatStr.includes(catStr) || catStr.includes(productCatStr))) {
      score += 15;
    }

    // Brand match
    if (brand && (product.brand || product.name || '').toLowerCase().includes(brand.toLowerCase())) {
      score += 20;
    }

    // Product name similarity
    if (productName && product.name) {
      const productNameLower = productName.toLowerCase();
      const dbNameLower = product.name.toLowerCase();
      if (dbNameLower.includes(productNameLower) || productNameLower.includes(dbNameLower)) {
        score += 25;
      }
    }

    return { product, score };
  });

  // Filter products with score > 0 and sort by score
  return scoredProducts
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(item => item.product);
};

export default {
  analyzeImage,
  searchMatchingProducts,
  extractFromFilename,
  buildImageSearchCatalogContext,
};
